import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import trainingService from '../api/services/trainingService';
import toast from 'react-hot-toast';

/**
 * Hook for managing quizzes
 */
export const useQuizzes = (params = {}) => {
    return useQuery({
        queryKey: ['quizzes', params],
        queryFn: async () => {
            const response = await trainingService.getQuizzes(params);
            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch quizzes');
            }
            return response;
        },
        placeholderData: (previousData) => previousData,
        staleTime: 0,
    });
};

/**
 * Hook for managing questions
 */
export const useQuestions = (params = {}) => {
    return useQuery({
        queryKey: ['questions', params],
        queryFn: async () => {
            const response = await trainingService.getQuestions(params);
            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch questions');
            }
            return response;
        },
        placeholderData: (previousData) => previousData,
        staleTime: 0,
    });
};

/**
 * Hook for fetching courses for dropdown
 */
export const useCoursesForDropdown = (options = { status: 'published', limit: 1000 }) => {
    return useQuery({
        queryKey: ['coursesDropdown', options],
        queryFn: async () => {
            const response = await trainingService.getCoursesForDropdown(options);
            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch courses');
            }
            return response.data?.courses || [];
        },
        staleTime: 0,
    });
};

/**
 * Mutation for creating a question
 */
export const useCreateQuestion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => trainingService.createQuestion(data),
        onSuccess: (response) => {
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                toast.success(response.message || 'Question created successfully');
            } else {
                toast.error(response.error || 'Failed to create question');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Error creating question');
        },
    });
};

/**
 * Mutation for updating a question
 */
export const useUpdateQuestion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => trainingService.updateQuestion(id, data),
        onSuccess: (response) => {
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                // Also invalidate quizzes since they might contain this question
                queryClient.invalidateQueries({ queryKey: ['quizzes'] });
                toast.success(response.message || 'Question updated successfully');
            } else {
                toast.error(response.error || 'Failed to update question');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Error updating question');
        },
    });
};

/**
 * Mutation for deleting a question
 */
export const useDeleteQuestion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => trainingService.deleteQuestion(id),
        onSuccess: (response) => {
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                queryClient.invalidateQueries({ queryKey: ['quizzes'] });
                toast.success(response.message || 'Question deleted successfully');
            } else {
                toast.error(response.error || 'Failed to delete question');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Error deleting question');
        },
    });
};

/**
 * Mutation for creating a quiz
 */
export const useCreateQuiz = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => trainingService.createQuiz(data),
        onSuccess: (response) => {
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['quizzes'] });
                toast.success(response.message || 'Quiz created successfully');
            } else {
                toast.error(response.error || 'Failed to create quiz');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Error creating quiz');
        },
    });
};

/**
 * Mutation for updating a quiz
 */
export const useUpdateQuiz = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => trainingService.updateQuiz(id, data),
        onSuccess: (response) => {
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['quizzes'] });
                toast.success(response.message || 'Quiz updated successfully');
            } else {
                toast.error(response.error || 'Failed to update quiz');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Error updating quiz');
        },
    });
};

/**
 * Mutation for deleting a quiz
 */
export const useDeleteQuiz = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => trainingService.deleteQuiz(id),
        onSuccess: (response) => {
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['quizzes'] });
                toast.success(response.message || 'Quiz deleted successfully');
            } else {
                toast.error(response.error || 'Failed to delete quiz');
            }
        },
        onError: (error) => {
            toast.error(error.message || 'Error deleting quiz');
        },
    });
};
